import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner, Form, Modal, Table } from 'react-bootstrap';
import { FaCogs, FaPlus, FaEdit, FaTrash, FaTags, FaCar, FaSearch, FaExclamationTriangle } from 'react-icons/fa';

import api from '../services/api';

// ✅ BRAND FORM MODAL COMPONENT
const BrandFormModal = ({ show, onHide, brand, onSuccess }) => {
    const [formData, setFormData] = useState({ brand: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (brand) {
            setFormData({ brand: brand.brand });
        } else {
            setFormData({ brand: '' });
        }
    }, [brand]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (brand) {
                await api.put(`/admin/brands/${brand._id}`, formData);
            } else {
                await api.post('/admin/brands', formData);
            }

            onSuccess();
            onHide();
        } catch (err) {
            setError(err.response?.data?.message || 'İşlem başarısız');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaTags className="me-2" />
                    {brand ? 'Marka Düzenle' : 'Yeni Marka Ekle'}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form.Group className="mb-3">
                        <Form.Label>Marka Adı</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Örn: Audi, BMW, Mercedes"
                            value={formData.brand}
                            onChange={(e) => setFormData({ brand: e.target.value })}
                            autoFocus
                            required
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onHide}>
                        İptal
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || !formData.brand.trim()}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Kaydediliyor...
                            </>
                        ) : (
                            <>
                                {brand ? <FaEdit className="me-1" /> : <FaPlus className="me-1" />}
                                {brand ? 'Güncelle' : 'Ekle'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};


// ✅ MODEL FORM MODAL COMPONENT
const ModelFormModal = ({ show, onHide, selectedBrand, model, onSuccess }) => {
    const [formData, setFormData] = useState({ name: '', type: '' });
    const [bodyTypes, setBodyTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) {
            fetchBodyTypes();
        }

        if (model) {
            setFormData({ name: model.name, type: model.type });
        } else {
            setFormData({ name: '', type: '' });
        }
    }, [show, model]);

    const fetchBodyTypes = async () => {
        try {
            const response = await api.get('/admin/body-types');
            setBodyTypes(response.data.bodyTypes);
        } catch (err) {
            console.error('Body types fetch error:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (model) {
                await api.put(`/admin/models/${model._id}`, formData);
            } else {
                await api.post(`/admin/brands/${selectedBrand._id}/models`, formData);
            }

            onSuccess();
            onHide();
        } catch (err) {
            setError(err.response?.data?.message || 'İşlem başarısız');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FaCar className="me-2" />
                    {model ? 'Model Düzenle' : `${selectedBrand?.brand} - Yeni Model Ekle`}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    <Form.Group className="mb-3">
                        <Form.Label>Model Adı</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Örn: A4, X5, C-Class"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            autoFocus
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Kasa Tipi</Form.Label>
                        <Form.Select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            required
                        >
                            <option value="">Kasa tipi seçin</option>
                            {bodyTypes.map(bodyType => (
                                <option key={bodyType.value} value={bodyType.value}>
                                    {bodyType.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onHide}>
                        İptal
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading || !formData.name.trim() || !formData.type}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Kaydediliyor...
                            </>
                        ) : (
                            <>
                                {model ? <FaEdit className="me-1" /> : <FaPlus className="me-1" />}
                                {model ? 'Güncelle' : 'Ekle'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ✅ MAIN COMPONENT
const AdminBrandManagement = () => {
    // State Management
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    // Search & Filter
    const [brandSearch, setBrandSearch] = useState('');
    const [modelSearch, setModelSearch] = useState('');

    // Modal States
    const [showBrandModal, setShowBrandModal] = useState(false);
    const [showModelModal, setShowModelModal] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [editingModel, setEditingModel] = useState(null);

    // Fetch brands on mount
    useEffect(() => {
        fetchBrands();
    }, []);

    // Fetch brands from API
    const fetchBrands = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await api.get('/admin/brands');
            const newBrands = response.data.brands;
            setBrands(newBrands);

            // Update selectedBrand if exists
            setBrands(prev => {
                if (selectedBrand?._id) {
                    const updated = newBrands.find(b => b._id === selectedBrand._id);
                    if (updated) {
                        setSelectedBrand(updated);
                    }
                } else if (newBrands.length > 0) {
                    setSelectedBrand(newBrands[0]);
                }
                return newBrands;
            });

        } catch (err) {
            setError(err.response?.data?.message || 'Markalar yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    }, [selectedBrand]);

    // Filtered brands and models
    const filteredBrands = brands.filter(brand =>
        brand.brand.toLowerCase().includes(brandSearch.toLowerCase())
    );

    const filteredModels = selectedBrand
        ? selectedBrand.models.filter(model =>
            model.name.toLowerCase().includes(modelSearch.toLowerCase())
        )
        : [];

    // Success handler for modals
    const handleSuccess = useCallback(() => {
        fetchBrands();
        setSuccessMessage('İşlem başarıyla tamamlandı!');
        setTimeout(() => setSuccessMessage(''), 3000);
    }, [fetchBrands]);

    // Delete handlers
    const handleDeleteBrand = async (brand) => {
        const confirmed = window.confirm(
            `"${brand.brand}" markasını silmek istediğinizden emin misiniz?\n\n` +
            `⚠️ ${brand.modelCount} model de silinecek!\n` +
            `(${Object.entries(brand.bodyTypeBreakdown).map(([type, count]) => `${count} ${type}`).join(', ')})`
        );

        if (!confirmed) return;

        try {
            await api.delete(`/admin/brands/${brand._id}`);

            if (selectedBrand?._id === brand._id) {
                setSelectedBrand(null);
            }

            handleSuccess();
        } catch (err) {
            setError(err.response?.data?.message || 'Marka silinirken hata oluştu');
        }
    };

    const handleDeleteModel = async (model) => {
        const confirmed = window.confirm(
            `"${model.name}" (${model.type}) modelini silmek istediğinizden emin misiniz?`
        );

        if (!confirmed) return;

        try {
            await api.delete(`/admin/models/${model._id}`);
            handleSuccess();
        } catch (err) {
            setError(err.response?.data?.message || 'Model silinirken hata oluştu');
        }
    };

    return (
        <Container className="py-5">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-1">
                        <FaCogs className="me-2" />
                        Marka & Model Yönetimi
                    </h2>
                    <p className="text-muted mb-0">
                        Toplam {brands.length} marka, {brands.reduce((sum, b) => sum + b.modelCount, 0)} model
                    </p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => {
                        setEditingBrand(null);
                        setShowBrandModal(true);
                    }}
                >
                    <FaPlus className="me-1" />
                    Yeni Marka
                </Button>
            </div>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <FaExclamationTriangle className="me-2" />
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" dismissible onClose={() => setSuccessMessage('')}>
                    {successMessage}
                </Alert>
            )}

            <Row>
                {/* Left Panel - Brands */}
                <Col lg={6}>
                    <Card className="mb-4">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <FaTags className="me-2" />
                                    Markalar ({filteredBrands.length})
                                </h5>
                                <Form.Control
                                    type="text"
                                    placeholder="Marka ara..."
                                    value={brandSearch}
                                    onChange={(e) => setBrandSearch(e.target.value)}
                                    style={{ width: '150px' }}
                                />
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center py-4">
                                    <Spinner animation="border" />
                                    <p className="mt-2">Yükleniyor...</p>
                                </div>
                            ) : filteredBrands.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <FaTags size={40} className="mb-3" />
                                    <p>Henüz marka eklenmemiş</p>
                                </div>
                            ) : (
                                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    {filteredBrands.map(brand => (
                                        <div
                                            key={brand._id}
                                            className={`p-3 border-bottom ${selectedBrand?._id === brand._id ? 'bg-light' : ''}`}
                                            onClick={() => setSelectedBrand(brand)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="mb-1 fw-bold">{brand.brand}</h6>
                                                    <small className="text-muted">
                                                        {brand.modelCount} model
                                                        {Object.keys(brand.bodyTypeBreakdown).length > 0 && (
                                                            <span className="ms-2">
                                                                ({Object.entries(brand.bodyTypeBreakdown)
                                                                    .map(([type, count]) => `${count} ${type}`)
                                                                    .join(', ')})
                                                            </span>
                                                        )}
                                                    </small>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingBrand(brand);
                                                            setShowBrandModal(true);
                                                        }}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteBrand(brand);
                                                        }}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Panel - Models */}
                <Col lg={6}>
                    <Card className="mb-4">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <FaCar className="me-2" />
                                    {selectedBrand ? `${selectedBrand.brand} Modelleri` : 'Modeller'}
                                    {selectedBrand && ` (${filteredModels.length})`}
                                </h5>
                                <div className="d-flex gap-2">
                                    <Form.Control
                                        type="text"
                                        placeholder="Model ara..."
                                        value={modelSearch}
                                        onChange={(e) => setModelSearch(e.target.value)}
                                        style={{ width: '150px' }}
                                    />
                                    {selectedBrand && (
                                        <Button
                                            variant="outline-success"
                                            size="sm"
                                            onClick={() => {
                                                setEditingModel(null);
                                                setShowModelModal(true);
                                            }}
                                        >
                                            <FaPlus />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {!selectedBrand ? (
                                <div className="text-center py-4 text-muted">
                                    <FaCar size={40} className="mb-3" />
                                    <p>Soldan bir marka seçin</p>
                                </div>
                            ) : filteredModels.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <FaCar size={40} className="mb-3" />
                                    <p>Bu markada henüz model yok</p>
                                    <Button
                                        variant="outline-primary"
                                        onClick={() => {
                                            setEditingModel(null);
                                            setShowModelModal(true);
                                        }}
                                    >
                                        <FaPlus className="me-1" />
                                        İlk modeli ekle
                                    </Button>
                                </div>
                            ) : (
                                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    {filteredModels.map(model => (
                                        <div
                                            key={model._id}
                                            className="p-3 border-bottom"
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="mb-1 fw-bold">{model.name}</h6>
                                                    <Badge bg="secondary" className="text-capitalize">
                                                        {model.type}
                                                    </Badge>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => {
                                                            setEditingModel(model);
                                                            setShowModelModal(true);
                                                        }}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteModel(model)}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modals */}
            <BrandFormModal
                show={showBrandModal}
                onHide={() => setShowBrandModal(false)}
                brand={editingBrand}
                onSuccess={handleSuccess}
            />

            <ModelFormModal
                show={showModelModal}
                onHide={() => setShowModelModal(false)}
                selectedBrand={selectedBrand}
                model={editingModel}
                onSuccess={handleSuccess}
            />
        </Container>
    );
};

export default AdminBrandManagement;